import type { CameraLucidaEvent, CameraLucidaFindingKind } from '../types/cameraLucidaTypes'
import type { PiMatrixAgentAgentStreamEvent } from '../types/piMatrixAgent'

const CAMERA_EVENT_TYPES = new Set([
  'agent.remark',
  'agent.read',
  'agent.consult.characters',
  'agent.consult.closed',
  'finding.filed',
  'reading.suggested',
  'inquiry.submitted',
  'inquiry.completed',
])

const FINDING_KINDS = new Set<CameraLucidaFindingKind>([
  'lacuna', 'displacement', 'affiliation', 'variation', 'structure', 'suggested',
])

const INSPECTION_PROGRESS: Record<string, { activity: string; text: string }> = {
  read_collation: { activity: 'READING · COLLATION', text: 'Reading the requested matrix scope.' },
  read_index: { activity: 'CONSULTING · INDEX', text: 'Consulting the requested scholarly index.' },
  merge: { activity: 'PROPOSING · MERGE', text: 'Preparing an approval-required merge proposal.' },
  split: { activity: 'PROPOSING · SPLIT', text: 'Preparing an approval-required split proposal.' },
  shift_boundary: { activity: 'PROPOSING · BOUNDARY SHIFT', text: 'Preparing an approval-required boundary proposal.' },
  shift_rows: { activity: 'PROPOSING · ROW SHIFT', text: 'Preparing an approval-required row proposal.' },
  mark: { activity: 'PROPOSING · MARK', text: 'Preparing an approval-required mark proposal.' },
  submit_inquiry: { activity: 'ASSEMBLING · REPORT', text: 'Assembling the inquiry report.' },
}

const EDIT_TOOL_NAMES = new Set(['merge', 'split', 'shift_boundary', 'shift_rows', 'mark'])

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value))
const strings = (value: unknown): string[] | null => Array.isArray(value) && value.every(item => typeof item === 'string') ? value : null
const nonEmptyStrings = (value: unknown): string[] | null => {
  const parsed = strings(value)
  return parsed?.length ? parsed : null
}

const validFinding = (finding: unknown): boolean => (
  isRecord(finding)
  && typeof finding.id === 'string'
  && typeof finding.kind === 'string'
  && FINDING_KINDS.has(finding.kind as CameraLucidaFindingKind)
  && typeof finding.title === 'string'
  && typeof finding.text === 'string'
  && typeof finding.createdAt === 'string'
  && isRecord(finding.scope)
  && Boolean(nonEmptyStrings(finding.scope.rowIds))
  && Boolean(nonEmptyStrings(finding.scope.witnessIds))
)

const validCameraEvent = (value: unknown): value is CameraLucidaEvent => {
  if (!isRecord(value) || typeof value.type !== 'string' || !CAMERA_EVENT_TYPES.has(value.type)) return false
  if (value.type === 'agent.remark') return typeof value.text === 'string'
  if (value.type === 'agent.read') {
    return isRecord(value.scope) && Boolean(nonEmptyStrings(value.scope.rowIds)) && Boolean(nonEmptyStrings(value.scope.witnessIds))
  }
  if (value.type === 'agent.consult.characters') return Boolean(nonEmptyStrings(value.rowIds))
  if (value.type === 'agent.consult.closed') return value.text === undefined || typeof value.text === 'string'
  if (value.type === 'inquiry.submitted') {
    return typeof value.summary === 'string'
      && Array.isArray(value.findings)
      && value.findings.every(validFinding)
      && (value.suggestion === null || validFinding(value.suggestion))
  }
  if (value.type === 'inquiry.completed') return typeof value.summary === 'string'
  if (value.type === 'finding.filed' || value.type === 'reading.suggested') {
    return validFinding(value.finding)
  }
  return false
}

export const cameraLucidaEventFromPiStream = (
  event: PiMatrixAgentAgentStreamEvent,
): CameraLucidaEvent | null => {
  if (event.type === 'agent.text.delta') return event
  if (event.type === 'tool.started') {
    const progress = INSPECTION_PROGRESS[event.toolName]
    if (!progress) return null
    const scope = event.observationScope
    const units = scope?.rowIds.length
      ? `${scope.rowIds.length} row${scope.rowIds.length === 1 ? '' : 's'} · ${scope.witnessIds.length} witness${scope.witnessIds.length === 1 ? '' : 'es'}`
      : ''
    return scope?.rowIds.length && scope.witnessIds.length
      ? { type: 'agent.inspect', scope, ...progress, ...(units ? { units } : {}) }
      : { type: 'agent.progress', ...progress }
  }
  if (event.type !== 'tool.completed' || event.isError || !isRecord(event.result)) return null
  if (event.toolName === 'read_collation') return null
  const details = event.result.details
  if (!isRecord(details)) return null
  if (validCameraEvent(details.cameraEvent)) return details.cameraEvent
  const progress = INSPECTION_PROGRESS[event.toolName]
  const scope = isRecord(details.observationScope) && strings(details.observationScope.rowIds) && strings(details.observationScope.witnessIds)
    ? { rowIds: strings(details.observationScope.rowIds) || [], witnessIds: strings(details.observationScope.witnessIds) || [] }
    : null
  return progress && scope?.rowIds.length && scope.witnessIds.length
    ? { type: 'agent.inspect', scope, ...progress, units: `${scope.rowIds.length} row${scope.rowIds.length === 1 ? '' : 's'} · ${scope.witnessIds.length} witness${scope.witnessIds.length === 1 ? '' : 'es'}` }
    : null
}

export const proposalIdFromPiStream = (event: PiMatrixAgentAgentStreamEvent): string | null => {
  if (event.type !== 'tool.completed' || event.isError || !EDIT_TOOL_NAMES.has(event.toolName) || !isRecord(event.result)) return null
  const content = event.result.content
  if (!Array.isArray(content) || !isRecord(content[0]) || typeof content[0].text !== 'string') return null
  try {
    const receipt = JSON.parse(content[0].text)
    return isRecord(receipt) && typeof receipt.proposalId === 'string' ? receipt.proposalId : null
  } catch {
    return null
  }
}

export const changeSetIdFromPiStream = (event: PiMatrixAgentAgentStreamEvent): string | null => {
  if (event.type !== 'tool.completed' || event.isError || !isRecord(event.result)) return null
  const details = event.result.details
  return isRecord(details) && typeof details.changeSetId === 'string' ? details.changeSetId : null
}

export const createCameraLucidaCompletionGate = (
  emit: (event: CameraLucidaEvent) => void,
): {
  forward: (event: CameraLucidaEvent) => void
  complete: (fallbackSummary: string) => string
} => {
  let protocolSummary = ''
  let completed = false

  return {
    forward(event) {
      if (event.type === 'inquiry.submitted') {
        if (protocolSummary) return
        event.findings.forEach(finding => emit({ type: 'finding.filed', finding }))
        if (event.suggestion) emit({ type: 'reading.suggested', finding: event.suggestion })
        protocolSummary = event.summary
        return
      }
      if (event.type === 'inquiry.completed') {
        protocolSummary = event.summary
        return
      }
      if (!completed) emit(event)
    },
    complete(_fallbackSummary) {
      const summary = protocolSummary.trim()
      if (!summary) {
        throw new Error('The inquiry ended before the agent supplied its report.')
      }
      if (!completed) {
        completed = true
        emit({ type: 'inquiry.completed', summary })
      }
      return summary
    },
  }
}

export const buildCameraLucidaAgentPrompt = (question: string): string => `You are working inside Variorum Camera Lucida, a watchable scholarly inquiry surface over the current collation snapshot.

Protocol:
- Camera Lucida automatically projects every tool call onto the plate. Visibility requires no Camera-specific command.
- Read the collation only through bounded, paginated read_collation calls. Use overview to survey and text for exact readings.
- Consult characters or another register through read_index only when that evidence bears on the question.
- Consult the Chapter Structure register through read_index when hierarchy, narrative level, scenes, tales, frames, or discourse boundaries bear on the question.
- Derive comparisons, drift, divergence, and explanations from those primitive reads; do not search for specialized analysis tools.
- Propose an edit only when the scholar explicitly asks for one. Immediately before merge, split, shift_boundary, shift_rows, or mark, make one exact text read_collation call containing precisely the affected scope. Every edit remains approval-required.
- When edits are requested, draft as many bounded edits as the evidence warrants. Each edit must follow its own exact read, and the ordered sequence is reviewed and approved as one transaction.
- Finish exactly once with submit_inquiry. Give it the final summary, no more than eight substantive coordinate-bearing findings, and at most one suggested next reading. Cite the filed ids inline as [F1], [F2], or [SUG].
- Never expose internal IDs or UUIDs in prose, finding titles, or the final report. Refer to rows by their scholarly labels and witnesses by sigla or shelfmark.
- Do not repeat the report in ordinary assistant prose after submit_inquiry.

Scholar's question:
${question}`
